import { useEditorEffect, useEditorEventListener } from "@handlewithcare/react-prosemirror";
import cx from "classnames";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";

import {
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
	usePresence,
	useUserById,
} from "@moment/state";

import { indicatorDisplayableInProseMirror } from "../easel/prosemirror/helpers";

interface Indicator {
	anchor: number;
	head: number;
	version: number;
	clientId: string;
	userId: string;
}

/** Pre-computed position data for a presence anchor */
interface IndicatorPosition {
	indicator: Indicator;
	top: number;
	left: number;
	shouldShow: boolean;
}

// Taken from https://www.tableau.com/blog/colors-upgrade-tableau-10-56782
export const PRESENCE_COLORS = [
	"#4e79a7",
	"#59a14f",
	"#9c755f",
	"#f28e2b",
	"#edc948",
	"#bab0ac",
	"#e15759",
	"#b07aa1",
	"#76b7b2",
	"#ff9da7",
] as const;

export function getPresenceColor(userId: string) {
	let index = 0;
	for (const char of userId) {
		index += char.charCodeAt(0);
	}
	return PRESENCE_COLORS[index % PRESENCE_COLORS.length]!;
}

function PresenceAnchor({
	position,
	container,
}: {
	position: IndicatorPosition;
	container: HTMLDivElement | null;
}) {
	const { indicator, top, left, shouldShow } = position;
	const userId = indicator.userId;
	const presenceColor = getPresenceColor(userId);
	const [expanded, setExpanded] = useState(false);

	const { user } = useUserById(userId);

	useEditorEventListener("mousemove", (view, event) => {
		const domBox = view.dom.getBoundingClientRect();
		const x = event.x - domBox.x;
		const y = event.y - domBox.y;
		setExpanded(x < left + 8 && x > left - 8 && y < top + 22 + 8 && y > top - 8);
	});

	if (!shouldShow) {
		return null;
	}

	return (
		<span
			className={cx(
				"flex-col -z-10 inline-flex w-0 h-[1.6em] -translate-y-1.5 absolute items-center"
			)}
			style={{ top, left }}
		>
			{container &&
				createPortal(
					<span
						className={cx(
							"w-1 h-1 rounded-sm absolute flex items-center justify-center -translate-y-1.5 -translate-x-1/2 shrink-0",
							{
								"w-max h-4 -translate-y-5 px-1": expanded,
							}
						)}
						style={{
							top,
							left,
							backgroundColor: presenceColor,
						}}
					>
						<span
							className={cx(
								"text-white font-semibold text-[0.6rem] leading-[0.6rem]",
								expanded ? "inline" : "hidden",
								!user?.name ? "italic" : ""
							)}
						>
							{user?.name ?? "Unknown user"}
						</span>
					</span>,
					container,
					indicator.clientId
				)}
			<span
				className="w-[2px] grow"
				style={{
					backgroundColor: presenceColor,
				}}
			/>
		</span>
	);
}

/**
 * Render the presence anchors according to their anchor positions.
 *
 * This can't be a widget decoration, because WebKit selection rendering gets
 * messed up when the selection directly abuts a widget decoration.
 */
export function PresenceAnchors() {
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageId } = usePageFromCurrentRoute();

	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const [positions, setPositions] = useState<IndicatorPosition[]>([]);

	const { indicators: mappedIndicators } = usePresence({ documentId, pageId });

	// Memoize anchor positions for dependency tracking
	const anchorPositions = useMemo(
		() => mappedIndicators?.map((i) => i.anchor) ?? [],
		[mappedIndicators]
	);

	// Batch all layout reads in a single effect - avoids layout thrashing
	useEditorEffect(
		(view) => {
			if (!mappedIndicators || mappedIndicators.length === 0) {
				setPositions([]);
				return;
			}

			// Single getBoundingClientRect call for all indicators
			const domBox = view.dom.getBoundingClientRect();

			// Batch all coordsAtPos calls and state computation
			const newPositions: IndicatorPosition[] = mappedIndicators.map((indicator) => {
				const shouldShow = indicatorDisplayableInProseMirror(view.state, indicator);
				const coords = view.coordsAtPos(indicator.anchor);
				return {
					indicator,
					top: coords.top - domBox.y,
					left: coords.left - domBox.x,
					shouldShow,
				};
			});

			setPositions(newPositions);
		},
		[anchorPositions]
	);

	return (
		<div ref={setContainer}>
			{positions.map((position) => (
				<PresenceAnchor
					key={position.indicator.clientId}
					container={container}
					position={position}
				/>
			))}
		</div>
	);
}
